import React, { useEffect, useState } from 'react';
import { Play, Pause, Hand, Footprints, MousePointer2 } from 'lucide-react';
import { Poem, RhythmPattern } from '../../types';
import { RHYTHM_PATTERNS } from '../../data/mockData';
import { playDrum } from '../../utils/audioUtils';
type RhythmGameProps = {
  poem: Poem;
};
export function RhythmGame({
  poem
}: RhythmGameProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentBeat, setCurrentBeat] = useState(0);
  const pattern = RHYTHM_PATTERNS[poem.timeSignature] || RHYTHM_PATTERNS['4/4'];
  useEffect(() => {
    let interval: number;
    if (isPlaying) {
      interval = window.setInterval(() => {
        setCurrentBeat(prev => {
          const next = (prev + 1) % pattern.length;
          // Play sound for the NEXT beat immediately
          const action = pattern[next].action;
          if (action === 'clap') playDrum('snare');else if (action === 'stomp') playDrum('kick');else if (action === 'tap') playDrum('woodblock');
          return next;
        });
      }, 800); // Slightly slower for kids
    } else {
      setCurrentBeat(0);
    }
    return () => clearInterval(interval);
  }, [isPlaying, pattern]);
  const getActionIcon = (action: string) => {
    switch (action) {
      case 'clap':
        return <Hand className="w-8 h-8" data-id="element-118" />;
      case 'stomp':
        return <Footprints className="w-8 h-8" data-id="element-119" />;
      case 'tap':
        return <MousePointer2 className="w-8 h-8" data-id="element-120" />;
      case 'pose':
        return <div className="text-2xl font-bold" data-id="element-121">★</div>;
      default:
        return <div className="w-8 h-8 rounded-full border-2 border-dashed border-gray-300" data-id="element-122" />;
    }
  };
  const getActionLabel = (action: string) => {
    switch (action) {
      case 'clap':
        return '拍手';
      case 'stomp':
        return '跺脚';
      case 'tap':
        return '拍桌';
      case 'pose':
        return '造型';
      default:
        return '休止';
    }
  };
  return <div className="flex flex-col items-center w-full max-w-4xl mx-auto" data-id="element-123">
      {/* Visualization Area */}
      <div className="w-full bg-white rounded-3xl shadow-xl p-8 mb-8 min-h-[300px] flex flex-col items-center justify-center relative overflow-hidden" data-id="element-124">
        <div className="absolute top-0 left-0 w-full h-2 bg-gray-100" data-id="element-125">
          <div className="h-full bg-[#F2BE45] transition-all duration-300 ease-linear" style={{
          width: `${(currentBeat + 1) / pattern.length * 100}%`
        }} data-id="element-126" />
        </div>

        <div className="grid grid-cols-4 gap-4 md:gap-8 w-full" data-id="element-127">
          {pattern.map((step, index) => {
          const isActive = isPlaying && currentBeat === index;
          return <div key={index} className={`flex flex-col items-center justify-center p-4 rounded-2xl transition-all duration-200 ${isActive ? 'bg-[#F2BE45] text-white scale-110 shadow-lg transform -translate-y-2' : 'bg-gray-50 text-gray-400'}`} data-id="element-128">
                <div className={`mb-2 ${isActive ? 'animate-bounce' : ''}`} data-id="element-129">
                  {getActionIcon(step.action)}
                </div>
                <span className="font-bold text-lg" data-id="element-130">
                  {getActionLabel(step.action)}
                </span>
                <span className="text-xs opacity-60 mt-1" data-id="element-131">
                  Beat {step.beat}
                </span>
              </div>;
        })}
        </div>

        {/* Central Feedback */}
        <div className="mt-12 text-center" data-id="element-132">
          <p className="text-gray-400 text-sm mb-2" data-id="element-133">当前动作</p>
          <div className="text-4xl font-bold text-[#C41E3A] h-12 transition-all duration-200 transform scale-100" data-id="element-134">
            {isPlaying ? getActionLabel(pattern[currentBeat].action) : '准备开始'}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center space-x-6" data-id="element-135">
        <button onClick={() => setIsPlaying(!isPlaying)} className={`flex items-center justify-center w-20 h-20 rounded-full shadow-lg transition-all transform hover:scale-105 ${isPlaying ? 'bg-gray-200 text-gray-700' : 'bg-[#C41E3A] text-white'}`} data-id="element-136">
          {isPlaying ? <Pause className="w-10 h-10" data-id="element-137" /> : <Play className="w-10 h-10 ml-1" data-id="element-138" />}
        </button>
      </div>

      <p className="mt-6 text-gray-500 text-center max-w-md" data-id="element-139">
        点击播放按钮，跟随节拍完成动作。
        <br data-id="element-140" />
        <span className="text-xs text-gray-400" data-id="element-141">
          提示：请确保设备音量已开启
        </span>
      </p>
    </div>;
}